import dayjs from 'dayjs'
import { combineEpics, Epic, ofType } from 'redux-observable'
import { EMPTY, filter, mergeMap, of } from 'rxjs'
import {
  createOpenFinAlert,
  isMainOpenFinWindow
} from '../../containers/OpenFin/helpers'
import { createStreamEpic } from '../../signalr/createStreamEpic'
import {
  createActivityFromCancelOrderAlert,
  createActivityFromCounteredToppedAlert,
  createActivityFromNewOrderAlert,
  createActivityFromOrder,
  createActivityFromTradingNowAlert
} from '../activity/helpers'
import { Activity } from '../activity/types'
import { AddOrUpdateUserOrdersAction } from '../order/actions'
import { fetchSecuritiesByIds } from '../securities/actions'
import { getSecurityStaticDataById } from '../securities/selectors'
import { getInitTime } from '../ws/selectors'
import {
  AddCancelOrderAlerts,
  AddCounteredToppedAlerts,
  AddNewOrderAlerts,
  AddTradingNowAlerts
} from './actions'
import {
  subscribeCancelOrderAlertsDefinition,
  subscribeCounteredToppedAlertsDefinition,
  subscribeNewOrderAlertsDefinition,
  subscribeTradingNowAlertsDefinition
} from './definition'

const fetchSecurityInfoForAlertsEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<AddTradingNowAlerts>('alerts.addTradingNowAlerts'),
    mergeMap((action) => {
      const { tradingNowAlerts } = action.payload
      const getSecurity = getSecurityStaticDataById(state$.value)
      const securityIds = tradingNowAlerts
        .map((alert) => alert.securityId)
        .filter((securityId) => getSecurity(securityId) === undefined)
      return securityIds.length > 0
        ? of(fetchSecuritiesByIds(securityIds))
        : EMPTY
    })
  )

const fetchSecurityInfoForNewOrderAlertsEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<AddNewOrderAlerts>('alerts.addNewOrderAlerts'),
    mergeMap((action) => {
      const { newOrderAlerts } = action.payload
      const getSecurity = getSecurityStaticDataById(state$.value)
      const securityIds = newOrderAlerts
        .map((alert) => alert.securityId)
        .filter((securityId) => getSecurity(securityId) === undefined)
      return securityIds.length > 0
        ? of(fetchSecuritiesByIds(securityIds))
        : EMPTY
    })
  )

const fetchSecurityInfoForCancelOrderAlertsEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<AddCancelOrderAlerts>('alerts.addCancelOrderAlerts'),
    mergeMap((action) => {
      const { cancelOrderAlerts } = action.payload
      const getSecurity = getSecurityStaticDataById(state$.value)
      const securityIds = cancelOrderAlerts
        .map((alert) => alert.securityId)
        .filter((securityId) => getSecurity(securityId) === undefined)
      return securityIds.length > 0
        ? of(fetchSecuritiesByIds(securityIds))
        : EMPTY
    })
  )

const processedIds = new Set<string>()
const openFinOrderAlertsEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<AddOrUpdateUserOrdersAction>('order.addOrUpdateUserOrders'),
    filter(() => isMainOpenFinWindow()),
    mergeMap((action) => {
      const activities: Activity[] = []
      action.payload.forEach((order) => {
        const security = getSecurityStaticDataById(state$.value)(
          order.securityId
        )
        if (
          [
            'accepted',
            'rejected',
            'cancelled',
            'waitingForConfirmation'
          ].includes(order.status) &&
          !processedIds.has(`${order.id}${order.status}`) &&
          dayjs(order.submitTime).isAfter(getInitTime(state$.value)) &&
          security
        ) {
          const activity = createActivityFromOrder(order, security)
          if (activity && !activity.hasAggressorActions) {
            activities.push(activity)
            processedIds.add(`${order.id}${order.status}`)
          }
        }
      })

      createOpenFinAlert(activities)
      return EMPTY
    })
  )

const openFinTradeAlertsEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<
      | AddTradingNowAlerts
      | AddNewOrderAlerts
      | AddCounteredToppedAlerts
      | AddCancelOrderAlerts
    >(
      'alerts.addTradingNowAlerts',
      'alerts.addNewOrderAlerts',
      'alerts.addCounteredToppedAlerts',
      'alerts.addCancelOrderAlerts'
    ),
    filter(() => isMainOpenFinWindow()),
    mergeMap((action) => {
      const activity: Activity[] = []
      const initTime = getInitTime(state$.value)
      switch (action.type) {
        case 'alerts.addTradingNowAlerts':
          const { tradingNowAlerts } = action.payload
          tradingNowAlerts.forEach(
            (alert) =>
              dayjs(alert.dateTime).isAfter(initTime) &&
              activity.push(createActivityFromTradingNowAlert(alert))
          )
          break
        case 'alerts.addNewOrderAlerts':
          const { newOrderAlerts } = action.payload
          newOrderAlerts.forEach(
            (alert) =>
              dayjs(alert.dateTime).isAfter(initTime) &&
              activity.push(createActivityFromNewOrderAlert(alert))
          )
          break
        case 'alerts.addCounteredToppedAlerts':
          const { counteredToppedAlerts } = action.payload
          counteredToppedAlerts.forEach(
            (alert) =>
              dayjs(alert.dateTime).isAfter(initTime) &&
              activity.push(createActivityFromCounteredToppedAlert(alert))
          )
          break
        case 'alerts.addCancelOrderAlerts':
          const { cancelOrderAlerts } = action.payload
          cancelOrderAlerts.forEach(
            (alert) =>
              dayjs(alert.dateTime).isAfter(initTime) &&
              activity.push(createActivityFromCancelOrderAlert(alert))
          )
          break
        default:
          break
      }
      createOpenFinAlert(activity)
      return EMPTY
    })
  )

export default combineEpics(
  createStreamEpic('alerts.subscribe', subscribeTradingNowAlertsDefinition),
  createStreamEpic(
    'alerts.subscribe',
    subscribeCounteredToppedAlertsDefinition
  ),
  createStreamEpic('alerts.subscribe', subscribeNewOrderAlertsDefinition),
  createStreamEpic('alerts.subscribe', subscribeCancelOrderAlertsDefinition),
  fetchSecurityInfoForAlertsEpic,
  fetchSecurityInfoForNewOrderAlertsEpic,
  fetchSecurityInfoForCancelOrderAlertsEpic,
  openFinOrderAlertsEpic,
  openFinTradeAlertsEpic
)
